import { FC, useEffect, useState, useMemo } from "react";
import { CommonSearch } from "@ui/common-search";
import { useSearch } from "@hooks/useSearch";
import { MedicalExamControlsTable } from "./medical-exam-controls-table";
import {
  useDownloadMedicalExamApplicantsMutation,
  useDownloadMedicalExamControlsMutation,
  useDownloadMedicalExamsEmployeesListMutation,
  useDownloadPeriodicMedicalExamsMutation,
  useMedicalExamControlsQuery,
  useMedicalExaminationFactorsQuery,
  useMedicalExaminationQuery,
  useUpdateMedicalExamControlsMutation,
} from "@lib/medical-examination";
import { useSelectedCompanyQuery } from "@lib/company";
import styles from "./medical-exam-controls.module.scss";
import { Pagination } from "@components/pagination";
import { SELECTS_ID } from "@store/company/procedure-instrunctions-store";
import {
  GetMedicalExamControlsParams,
  MedicalExamControlsBody,
  MedicalExamControlsT,
} from "@interfaces/medical-examination";
import {
  MedicalExamControlsFilters,
  SelectList,
} from "./medical-exam-controls-filters";
import { convertToSelectList } from "@utils/convertToSelectList";
import { useSorting } from "@hooks/useSorting";
import { useQueryClient } from "react-query";
import { medicalExaminationQueryKeys } from "@lib/medical-examination/queryKeys";
import { downloadBlob } from "@utils/downloadBlob";
import { FileLoadSVG } from "@components/svgs";
import toast from "react-hot-toast";
import { useDepartmentsGetAllQuery } from "@lib/departments-get-all/departments-get-all";
import { usePositionsGetAllQuery } from "@lib/positions-get-all/positions-get-all";
import { useEmployeesGetAllQuery } from "@lib/employees-get-all/employees-get-all";
import { getProcedureControlParamsArray } from "@utils/getProcedureControlParamsArray";

const LIMIT = 20;
const getOffset = (page: number) => LIMIT * (page - 1);

const sortingKeys = [
  "lastDateOrder",
  "departmentTitleOrder",
  "positionTitleOrder",
  "employeeNameOrder",
  "factorTitleOrder",
  "nextDateOrder",
  "daysBeforeNextDateOrder",
];

export type MedicalExamControlsPropsT = {};

export const MedicalExamControls: FC<MedicalExamControlsPropsT> = () => {
  const { company } = useSelectedCompanyQuery();

  const queryClient = useQueryClient();
  const updateControlsMutation = useUpdateMedicalExamControlsMutation();
  const downloadControlsMutation = useDownloadMedicalExamControlsMutation();
  const downloadApplicantsListMutation =
    useDownloadMedicalExamApplicantsMutation();
  const downloadPeriodicExamsMutation =
    useDownloadPeriodicMedicalExamsMutation();
  const downloadEmployeesListMutation =
    useDownloadMedicalExamsEmployeesListMutation();

  const [page, setPage] = useState(1);
  const search = useSearch();
  const [selectedCategory, setSelectedCategory] = useState<SelectList[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<SelectList[]>(
    []
  );
  const [selectedPositions, setSelectedPositions] = useState<SelectList[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectList[]>([]);
  const [selectedFactors, setSelectedFactors] = useState<SelectList[]>([]);
  const [startDate, setStartDate] = useState<number[]>();
  const [deadline, setDeadline] = useState<number[]>();

  const { data: departments } = useDepartmentsGetAllQuery(
    {
      companies: company ? [company.id] : undefined,
    },
    !!company
  );
  const departmentsList = convertToSelectList(
    departments ?? [],
    (item) => item.id,
    (item) => item.title ?? String(item.id)
  );

  const { data: positions } = usePositionsGetAllQuery(
    {
      companies: company ? [company.id] : undefined,
      departments: selectedDepartments.map((d) => d.title),
    },
    !!company
  );
  const positionsList = convertToSelectList(
    positions ?? [],
    (item) => item.id,
    (item) => item.title ?? String(item.id),
    (position) => position.parentId === null
  );

  const { data: employees } = useEmployeesGetAllQuery(
    {
      companies: company ? [company.id] : undefined,
      departments: selectedDepartments.map((d) => d.title),
      positions: selectedPositions.map((p) => p.title),
    },
    !!company
  );
  const employeesList = convertToSelectList(
    employees ?? [],
    (item) => item.id,
    (item) => item.name ?? String(item.id)
  );

  const { data: factorsData } = useMedicalExaminationFactorsQuery();
  const factors = factorsData?.map((item) => item.factors).flat();
  const factorsListData = convertToSelectList(
    factors ?? [],
    (item) => item.id,
    (item) => item.title
  );
  const factorsList = useMemo(() => {
    return [
      {
        title: "Все факторы",
        id: SELECTS_ID,
      },
      ...(factorsListData?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factorsListData]);

  const { data: categories } = useMedicalExaminationQuery();
  const categoriesList = categories?.filter(
    (category) => typeof category.id !== "undefined"
  );
  const categoriesListData = categoriesList?.map(
    (e) => ({ id: e.id, title: e.title }) as SelectList
  );
  useEffect(() => {
    if (categories) {
      setSelectedCategory(
        categories?.map((e) => ({ id: e.id, title: e.title }) as SelectList)
      );
    }
  }, [categories]);

  const sorting = useSorting(sortingKeys, "ASC");

  const params: GetMedicalExamControlsParams = {
    companyId: company?.id,
    offset: getOffset(page),
    limit: LIMIT,
    categories: selectedCategory ? selectedCategory.map((e) => e.id) : [],
    searchString: search.debounced,
    isRequired: true,
    departments: getProcedureControlParamsArray(
      selectedDepartments,
      departments?.length ?? 0,
      false
    ),
    positions: getProcedureControlParamsArray(
      selectedPositions,
      positions?.length ?? 0,
      false
    ),
    employees: getProcedureControlParamsArray(
      selectedEmployees,
      employees?.length ?? 0,
      false
    ),
    factors: getProcedureControlParamsArray(
      selectedFactors,
      factors?.length ?? 0,
      false
    ),
    date: startDate,
    deadline,
    ...sorting.getActiveSorting(),
  };
  const { data, isLoading } = useMedicalExamControlsQuery(params);
  let dataView: any = [];
  data?.forEach((e) => {
    if (e.body.length) dataView = dataView.concat(e.body);
  });

  const hasNextPage = (dataView.length ?? 0) >= LIMIT;

  const handleUpdateLastDate = (
    row: MedicalExamControlsBody,
    lastDate: string | null
  ) => {
    if (!company) return;

    updateControlsMutation.mutate(
      {
        companyId: company.id,
        id: row.id,
        lastDate,
      },
      {
        onSuccess: (result) => {
          toast.success("Данные обновлены");
          queryClient.setQueryData<MedicalExamControlsT[] | null>(
            medicalExaminationQueryKeys.controls(params),
            (state) => {
              if (!state) return null;

              return state.map((table) => {
                table.body = table.body.map((_row) =>
                  _row.id === row.id ? { ..._row, ...result } : _row
                );
                return table;
              });
            }
          );
        },
      }
    );
  };

  const getExcelFileName = () => {
    if (!company || typeof selectedCategory === "undefined") return;

    let filename = "";
    if (selectedCategory) {
      filename = `Контроль медицинских осмотров [${
        selectedCategory.length > 1
          ? selectedCategory.map((e) => e.title).join(", ")
          : selectedCategory[0].title
      }].xlsx`;
    } else {
      filename = `${company.name} - Контроль медицинских осмотров.xlsx`;
    }

    return filename;
  };

  const handleDownloadTable = () => {
    downloadControlsMutation.mutate(params, {
      onSuccess: (blob) => {
        const filename = getExcelFileName();
        if (!filename) return;
        downloadBlob(blob, filename);
      },
    });
  };

  const handleDownloadApplicantsList = () => {
    if (!company) return;

    downloadApplicantsListMutation.mutate(company.id, {
      onSuccess: (blob) => {
        downloadBlob(blob, `${company.name} - Поступающие на работу.xlsx`);
      },
    });
  };

  const handleDownloadPeriodicExams = () => {
    if (!company) return;

    downloadPeriodicExamsMutation.mutate(company.id, {
      onSuccess: (blob) => {
        downloadBlob(
          blob,
          `${company.name} - Работники, подлежающие периодическому МО.xlsx`
        );
      },
    });
  };

  const handleDownloadEmployeesList = () => {
    if (!company) return;

    downloadEmployeesListMutation.mutate(company.id, {
      onSuccess: (blob) => {
        downloadBlob(blob, `${company.name} - Список работников.xlsx`);
      },
    });
  };

  return (
    <div className={styles.medicalExamControls}>
      <CommonSearch
        className="ml-auto"
        onChange={() => {}}
        setSearch={search.setValue}
      />
      <div className={styles.medicalExamControls__content}>
        <div className={styles.medicalExamControls__downloadActions}>
          <button
            className={styles.medicalExamControls__downloadBtn}
            onClick={handleDownloadTable}
          >
            <FileLoadSVG />
            Скачать текущую таблицу
          </button>
          <button
            className={styles.medicalExamControls__downloadBtn}
            onClick={handleDownloadApplicantsList}
          >
            <FileLoadSVG />
            Список лиц, поступающих на работу
          </button>
          <button
            className={styles.medicalExamControls__downloadBtn}
            onClick={handleDownloadPeriodicExams}
          >
            <FileLoadSVG />
            Список работников, подлежащих периодическому МО
          </button>
          <button
            className={styles.medicalExamControls__downloadBtn}
            onClick={handleDownloadEmployeesList}
          >
            <FileLoadSVG />
            Поименный список
          </button>
        </div>
        <MedicalExamControlsFilters
          departments={departmentsList}
          positions={positionsList}
          employees={employeesList}
          factors={factorsList}
          categories={categoriesListData}
          selectedDepartments={selectedDepartments}
          selectedPositions={selectedPositions}
          selectedEmployees={selectedEmployees}
          selectedFactors={selectedFactors}
          selectedCategory={selectedCategory}
          onDepartmentsChange={(values) => {
            setSelectedDepartments(values);
            setSelectedPositions([]);
            setSelectedEmployees([]);
          }}
          onPositionChange={(values) => {
            setSelectedPositions(values);
            setSelectedEmployees([]);
          }}
          onEmployeeChange={setSelectedEmployees}
          onFactorChange={setSelectedFactors}
          onStartDateChange={setStartDate}
          onDeadlineChange={setDeadline}
          onCategoriesChange={setSelectedCategory}
        />
        {company && (
          <div>
            {/* Этот участок могут вернуть */}
            {/* <Text size={30} color="green" weight="bold" mt={48} mb={24}>
            { selectedCategory?.map(e => e.title).join(", ") }
            </Text> */}
            <MedicalExamControlsTable
              data={dataView ?? []}
              isLoading={isLoading}
              offset={params.offset}
              sorting={sorting}
              onUpdateLastDate={handleUpdateLastDate}
            />
            <div className={styles.medicalExamControls__pagination}>
              <Pagination
                value={page}
                onChange={setPage}
                total={hasNextPage ? page + 1 : page}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
